/**
 * FctLayout — layout completo corporativo (Header + Sider opcional + Content + Footer).
 *
 * Sigue las convenciones visuales de las aplicaciones internas de FCT:
 * cabecera fija con logo, sider colapsable opcional con el menú principal,
 * área de contenido con fondo gris claro, y footer discreto con copyright.
 *
 * Uso:
 *   <FctLayout
 *     appName="Gescom"
 *     menuItems={[...]}
 *     onMenuClick={(key) => navigate(key)}
 *     selectedKeys={[currentRoute]}
 *   >
 *     <YourPageContent />
 *   </FctLayout>
 */

import React, { useState } from 'react';
import { Layout, Menu, type MenuProps } from 'antd';
import { fctColors } from '../../theme/theme';
import { FctHeader } from './FctHeader';

const { Sider, Content, Footer } = Layout;

interface FctLayoutProps {
  children: React.ReactNode;
  /** Nombre de la aplicación interna (se muestra a la derecha del logo). */
  appName?: string;
  /** Items del menú lateral. Si no se pasan, no se renderiza el Sider. */
  menuItems?: MenuProps['items'];
  /** Callback al hacer clic en un item del menú. */
  onMenuClick?: MenuProps['onClick'];
  /** Keys actualmente seleccionadas en el menú. */
  selectedKeys?: string[];
  /** Contenido a renderizar en la zona derecha del header. */
  headerRightContent?: React.ReactNode;
  /** URL alternativa para el logo del header. */
  logoSrc?: string;
}

export const FctLayout: React.FC<FctLayoutProps> = ({
  children,
  appName,
  menuItems,
  onMenuClick,
  selectedKeys,
  headerRightContent,
  logoSrc,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const hasSider = Boolean(menuItems && menuItems.length > 0);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <FctHeader
        appName={appName}
        logoSrc={logoSrc}
        rightContent={headerRightContent}
      />
      <Layout>
        {hasSider && (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            theme="light"
            width={240}
            style={{
              borderRight: `1px solid ${fctColors.grisBorde}`,
            }}
          >
            <Menu
              mode="inline"
              items={menuItems}
              onClick={onMenuClick}
              selectedKeys={selectedKeys}
              style={{ borderRight: 0, paddingTop: 8 }}
            />
          </Sider>
        )}
        <Layout>
          <Content
            style={{
              padding: 24,
              background: fctColors.grisFondo,
              minHeight: 280,
            }}
          >
            {children}
          </Content>
          <Footer
            style={{
              textAlign: 'center',
              color: fctColors.grisCorporativo,
              background: fctColors.blanco,
              borderTop: `1px solid ${fctColors.grisBorde}`,
              padding: '16px 24px',
              fontSize: 12,
            }}
          >
            © {new Date().getFullYear()} Farmaconsulting Transacciones, S.L. ·
            Todos los derechos reservados
          </Footer>
        </Layout>
      </Layout>
    </Layout>
  );
};
